import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Camera, Plus, X, ArrowRight, Loader2 } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { usePhotos, useUploadPhoto, useDeletePhoto } from "../hooks/useProfile";
import { PhotoEditor } from "../components/PhotoEditor";

export function OnboardingPhotos() {
  const { data: photos = [], isLoading } = usePhotos();
  const upload = useUploadPhoto();
  const remove = useDeletePhoto();
  const [error, setError] = useState<string | null>(null);
  const [editingFile, setEditingFile] = useState<File | null>(null);

  const slots = 6;
  const canContinue = photos.length >= 2;

  function handlePick() {
    setError(null);
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/webp";
    input.onchange = () => {
      const f = input.files?.[0];
      if (!f) return;
      setEditingFile(f);
    };
    input.click();
  }

  async function handleEditorConfirm(blob: Blob) {
    setError(null);
    const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
    setEditingFile(null);
    try {
      await upload.mutateAsync(file);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Upload fehlgeschlagen. Nochmal versuchen.",
      );
    }
  }

  async function handleRemove(photoId: string) {
    try {
      await remove.mutateAsync(photoId);
    } catch {
      setError("Löschen fehlgeschlagen.");
    }
  }

  return (
    <AppShell hideNav>
      <section className="container max-w-2xl py-10">
        <div className="mb-8 flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-gradient shadow-glow">
            <Camera className="h-5 w-5 text-white" />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-500">
              Schritt 2 von 4
            </p>
            <h1 className="text-2xl font-bold tracking-tight">Deine Fotos</h1>
          </div>
        </div>

        <div className="card p-6 md:p-8">
          <p className="text-[15px] text-ink-500">
            Lade mindestens 2 Fotos von dir hoch. Zeig dich natürlich — die
            echten Matches entstehen, wenn du du selbst bist. Alle Fotos werden
            vor Freischaltung geprüft.
          </p>

          {error && (
            <div
              role="alert"
              className="mt-4 rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-700"
            >
              {error}
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {Array.from({ length: slots }).map((_, idx) => {
              const photo = photos[idx];
              if (photo) {
                return (
                  <div
                    key={photo.id}
                    className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-surface"
                  >
                    <img
                      src={photo.url}
                      alt={`Foto ${idx + 1}`}
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemove(photo.id)}
                      aria-label="Foto entfernen"
                      className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-ink-900/80 text-white backdrop-blur-sm transition hover:bg-ink-900"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    {idx === 0 && (
                      <span className="absolute left-2 top-2 rounded-full bg-brand-gradient px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-white">
                        Haupt
                      </span>
                    )}
                    {photo.status === "pending" && (
                      <span className="absolute inset-x-2 bottom-2 rounded-xl bg-ink-900/80 px-2 py-1 text-center text-[10px] font-semibold text-white backdrop-blur-sm">
                        Prüfung läuft
                      </span>
                    )}
                    {photo.status === "rejected" && (
                      <span className="absolute inset-x-2 bottom-2 rounded-xl bg-brand-500/90 px-2 py-1 text-center text-[10px] font-semibold text-white backdrop-blur-sm">
                        Abgelehnt
                      </span>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={`slot-${idx}`}
                  type="button"
                  disabled={upload.isPending || isLoading}
                  onClick={handlePick}
                  className="flex aspect-[3/4] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border bg-surface text-ink-500 transition hover:border-brand-400 hover:bg-brand-50/50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {upload.isPending && idx === photos.length ? (
                    <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
                  ) : (
                    <Plus className="h-6 w-6" />
                  )}
                  <span className="text-xs font-semibold">Hinzufügen</span>
                </button>
              );
            })}
          </div>

          <div className="mt-8 flex flex-col-reverse gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-ink-500">
              <span className="font-semibold text-ink-900">
                {photos.length}
              </span>{" "}
              / 6 Fotos
            </p>
            <Link
              to="/onboarding/werte"
              className={
                canContinue
                  ? "btn-primary"
                  : "btn-primary pointer-events-none opacity-50"
              }
            >
              Weiter
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-ink-500">
          <Heart className="mb-0.5 mr-1 inline h-3 w-3 text-brand-500" fill="currentColor" />
          Dein Hauptfoto ist das erste, das andere sehen.
        </p>
      </section>

      {editingFile && (
        <PhotoEditor
          file={editingFile}
          onConfirm={handleEditorConfirm}
          onCancel={() => setEditingFile(null)}
        />
      )}
    </AppShell>
  );
}
